import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Translate } from 'react-localize-redux';
import { ImageAssessmentValue } from './ImageAssessment';
import { QcCategoriesState, sectionsOf } from './qcCategories';

interface AssessmentStatusProps {
    /** The categories for this image's field, to name the first one ticked. */
    categories: QcCategoriesState;
    value: ImageAssessmentValue;
}

/**
 * SKIN-784 — an image card's one-line account of its assessment, now that the assessment
 * itself happens in the lesion's review dialog.
 *
 * The card's frame already states the outcome by colour; this states it in words and, for an
 * image with feedback, names the first item and how many more there are, so a reviewer
 * scanning the gallery learns why a frame is orange without opening the dialog.
 *
 * A stored `needs` with nothing attached — what the old toggle could produce — reads "Needs
 * feedback": that is what the server holds, and the line does not reinterpret it.
 */
export default function AssessmentStatus({ categories, value }: AssessmentStatusProps) {
    const { outcome, categoryIds, comment } = value;
    const hasNote = comment.trim() !== '';

    let first: string | null = null;
    if (outcome === 'needs' && categoryIds.length > 0) {
        const id = categoryIds[0];
        const field = sectionsOf(categories).flatMap((section) => section.fields).find((f) => f.id === id);
        // A category retired after it was given still names itself, as the static chips do.
        first = field?.label ?? `#${id}`;
    }
    const more = first === null ? 0 : categoryIds.length - 1 + (hasNote ? 1 : 0);

    const colour = outcome === 'correct' ? 'success.dark' : outcome === 'needs' ? 'warning.dark' : 'text.secondary';

    return (
        <Box
            data-testid="assessment-status"
            data-outcome={outcome ?? 'unassessed'}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: colour, minWidth: 0 }}
        >
            {outcome === 'correct' && <CheckCircleOutlineIcon fontSize="small" />}
            {outcome === 'needs' && <ErrorOutlineIcon fontSize="small" />}
            <Typography variant="body2" sx={{ fontWeight: outcome ? 600 : 400, minWidth: 0 }} noWrap>
                {first !== null ? (
                    <React.Fragment>
                        <span data-testid="assessment-status-first">{first}</span>
                        {more > 0 && <span data-testid="assessment-status-more">{` +${more}`}</span>}
                    </React.Fragment>
                ) : (
                    <Translate
                        id={outcome === 'correct'
                            ? 'hospital.quality-check.review.correct'
                            : outcome === 'needs'
                                ? 'hospital.quality-check.review.needs-feedback'
                                : 'hospital.quality-check.review.unassessed'}
                    />
                )}
            </Typography>
        </Box>
    );
}
